import { apiSlice } from './apiSlice';
import { PlaceBetPayload } from '../../types/Bet';

export interface GetCasinoGamesParams {
  search?: string;
  provider?: string;
  category?: string;
  page?: number;
  limit?: number;
  userId?: number | string;
  status?: string;
  ProviderId?: number;
  _gameType?: string;
}

export interface CasinoGame {
  id: number;
  casinoProviderId: number;
  category: string;
  code: string;
  createdAt: string;
  externalId: string;
  gameImage: string;
  gameProviderName: string;
  isTrending: boolean;
  isFavorite: boolean;
  name: string;
  priority: number;
  status: string;
}

export interface GetCasinoGamesResponse {
  success: boolean;
  message: string;
  games: CasinoGame[];
  pagination: {
    currentPage: number;
    limit: number;
    total: number;
    totalPage: number;
  };
}

export interface CasinoRound {
  id: number;
  gameId: number;
  gameName: string;
  roundId: string;
  txnId: string;
  userId: string;
  status: 'Won' | 'Lost' | 'Pending';
  totalBets: string;
  totalWins: string;
  totalLosses: string;
  completed: boolean;
  createdAt: string; // ISO date
  updatedAt: string; // ISO date

  user: {
    id: string;
    username: string;
    meta: {
      id: string;
      googleId: string | null;
      passwordSalt: string;
      passwordHash: string;
      transactionCode: string | null;
      mfaSecret: string | null;
      isMfaEnabled: boolean;
      userId: string;
      casinoToken: string;
      uplineId: string;
    };
  };

  casinoGame: {
    id: number;
    name: string;
  };
}

export interface Pagination {
  totalPage: number;
  currentPage: number;
  limit: number;
  totalItems: number;
}

export interface CasinoRoundHistoryResponse {
  success: boolean;
  rounds: CasinoRound[];
  pagination: Pagination;
}

interface CasinoRoundHistoryPayload {
  search?: string;
  provider?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface CreateCasinoSessionRequest {
  id: number;
  platform: 'Mobile' | 'Desktop';
}

export interface CreateCasinoSessionResponse {
  success: boolean;
  status: string;
  message: string;
  url?: string;
}

// Home Page API
export interface HomePageCasino {
  id: number;
  name: string;
  gameImage: string;
}

export interface HomePageCasinoResponse {
  success: boolean;
  message: string;
  games: {
    result: HomePageCasino[];
    count: number;
    page: number;
    limit: number;
  };
}

export interface CasinoGameProvider {
  id: number;
  name: string;
  providerType: string;
  settings: any | null;
  externalId: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CasinoProviderResponse {
  providers: CasinoProvider[];
  success: boolean;
  pagination: Pagination;
}

export interface CasinoProvider {
  provider: string;
  aggregator: string;
  status: 'Active' | 'Inactive' | string;
}

export interface CasinoGamesCountResponse {
  success: boolean;
  message: string;
  games: { category: string; count: number }[];
}

export interface FavoriteGamesResponse {
  message: string;
  games: {
    id: number;
    userId: string;
    gameId: number;
    eventId: number | null;
    sport: string;
    createdAt: string;
    game: {
      id: number;
      name: string;
      code: string;
      casinoProviderId: number;
      gameImage: string;
      gameProviderName: string;
      priority: number;
      status: string;
    };
  }[];
  pagination: {
    currentPage: number;
    totalPage: number;
    totalItems: number;
    limit: number;
  };
}

export const casinoApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCasinoGames: builder.query<GetCasinoGamesResponse, GetCasinoGamesParams>({
      query: ({
        search,
        provider,
        category,
        page,
        limit,
        userId,
        status = 'ACTIVE',
        ProviderId,
      } = {}) => {
        const params = new URLSearchParams();

        if (search) params.append('search', search);
        if (provider) params.append('provider', provider);
        if (category) params.append('category', category);
        if (page) params.append('page', page.toString());
        if (limit) params.append('limit', limit.toString());
        if (userId) params.append('userId', userId.toString());
        if (status) params.append('status', status);
        if (ProviderId) params.append('ProviderId', ProviderId.toString());

        return {
          url: `/casino/games${params.toString() ? `?${params.toString()}` : ''}`,
          method: 'GET',
          params: { t: Date.now() },
        };
      },
      providesTags: ['Casino'],
    }),

    getHomePageCasinoGames: builder.query<HomePageCasinoResponse, { limit: number; page: number }>({
      query: ({ limit = 80, page = 1 }) => {
        return {
          url: '/casino/static/games',

          method: 'GET',
          params: { t: Date.now(), limit: limit.toString(), page: page.toString() },
        };
      },
    }),

    getCasinoCategories: builder.query<
      { games: string[] },
      { search?: string; provider?: string; providerId?: string }
    >({
      query: ({ search, provider, providerId } = {}) => {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        if (provider) params.append('provider', provider);
        if (providerId) params.append('providerId', providerId);

        return {
          url: `/casino/category${params.toString() ? `?${params.toString()}` : ''}`,
          method: 'GET',
          params: { t: Date.now() },
        };
      },
      // providesTags: ['CasinoCategories'],
    }),

    getCasinoGameProviders: builder.query<CasinoGameProvider[], void>({
      query: () => {
        return {
          url: `sports-provider/casino`,
          method: 'GET',
          params: { t: Date.now() },
        };
      },
      // providesTags: ['CasinoProviders'],
    }),
    getCasinoProviders: builder.query<
      CasinoProviderResponse,
      { search?: string; aggregator?: string; page?: number; limit?: number; providerId?: number }
    >({
      query: ({ search, aggregator, page, limit, providerId } = {}) => {
        const params = new URLSearchParams();

        if (search) params.append('search', search);
        if (aggregator) params.append('aggregator', aggregator);
        if (page) params.append('page', page.toString());
        if (limit) params.append('limit', limit.toString());
        if (providerId) params.append('providerId', providerId.toString());

        return {
          url: `/casino/providers${params.toString() ? `?${params.toString()}` : ''}`,
          method: 'GET',
          params: { t: Date.now() },
        };
      },
      // providesTags: ['CasinoProviders'],
    }),

    getCasinoRoundHistory: builder.query<CasinoRoundHistoryResponse, CasinoRoundHistoryPayload>({
      query: ({ search, provider, status, startDate, endDate, page = 1, limit = 10 }) => {
        const params = new URLSearchParams();

        if (search) params.append('search', search);
        if (provider) params.append('provider', provider);
        if (status) params.append('status', status);
        if (startDate) params.append('fromDate', startDate);
        if (endDate) params.append('toDate', endDate);
        params.append('page', page.toString());
        params.append('limit', limit.toString());

        return {
          url: `/casino/round/history?${params.toString()}`,
          method: 'GET',
          params: { t: Date.now() },
        };
      },
      // providesTags: ['CasinoRoundHistory'],
    }),

    createCasinoSession: builder.mutation<CreateCasinoSessionResponse, CreateCasinoSessionRequest>({
      query: ({ id, platform }) => ({
        url: `/casino/create-session/${id}`,
        method: 'POST',
        body: { platform },
      }),
      //   invalidatesTags: ['CasinoSession'],
    }),
    getLobbyUrlById: builder.query<{ sessionId: string; url: string }, { userId: number }>({
      query: ({ userId }) => ({
        url: `/casino/lobby/${userId}`,
        method: 'GET',
      }),
      //   invalidatesTags: ['CasinoSession'],
    }),
    launchGame: builder.mutation<CreateCasinoSessionResponse, { id: number; platform: string }>({
      query: ({ id, platform }) => ({
        url: `/casino/gamelauncher/${id}`,
        method: 'POST',
        body: { platform },
      }),
      //   invalidatesTags: ['CasinoSession'],
    }),
    getCasinoGamesCount: builder.query<CasinoGamesCountResponse, void>({
      query: () => ({
        url: `/casino/meta/countgame`,
        method: 'GET',
      }),
    }),
    getFavouriteCasinoGames: builder.query<FavoriteGamesResponse, { page: number; limit: number }>({
      query: ({ page, limit }) => ({
        url: `/casino/favorite`,
        method: 'GET',
        params: {
          page,
          limit,
        },
      }),
      providesTags: ['Casino'],
    }),
    updateCasinoFavourite: builder.mutation<any, { id: number; status: string }>({
      query: ({ id, status }) => ({
        url: `/casino/favorite/${id}`,
        method: 'PUT',
        body: {
          status,
        },
      }),
      invalidatesTags: ['Casino'],
    }),
  }),
});

export const {
  useGetCasinoGamesQuery,
  useGetCasinoCategoriesQuery,
  useGetCasinoRoundHistoryQuery,
  useCreateCasinoSessionMutation,
  useGetHomePageCasinoGamesQuery,
  useGetLobbyUrlByIdQuery,
  useLaunchGameMutation,
  useGetCasinoGameProvidersQuery,
  useGetCasinoProvidersQuery,
  useGetCasinoGamesCountQuery,
  useUpdateCasinoFavouriteMutation,
  useGetFavouriteCasinoGamesQuery,
} = casinoApi;
